import { listDirectorySkills, listPersonalSkills } from "./client";
import type { SkillsCatalog } from "./types";

type Listener = () => void;

export const SKILLS_CATALOG_REFRESH_COOLDOWN_MS = 5_000;

let userScope: string | null = null;
let cachedCatalog: SkillsCatalog | undefined;
let inFlightPromise: Promise<SkillsCatalog> | null = null;
let lastFetchedAt = 0;
let storeGeneration = 0;
let storeVersion = 0;
const listeners = new Set<Listener>();

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

function clearCatalog(): void {
  cachedCatalog = undefined;
  inFlightPromise = null;
  lastFetchedAt = 0;
  storeGeneration += 1;
  storeVersion += 1;
  notifyListeners();
}

/** Drops a cached or failed catalog so the next `ensureSkillsCatalog` refetches. */
export function invalidateSkillsCatalog(): void {
  clearCatalog();
}

export function getSkillsCatalogUserScope(): string | null {
  return userScope;
}

export function setSkillsCatalogUserScope(
  nextUserScope: string | null | undefined
): void {
  const normalizedScope = nextUserScope?.trim() || null;
  if (userScope === normalizedScope) return;
  userScope = normalizedScope;
  clearCatalog();
}

export function getCachedSkillsCatalog(): SkillsCatalog | undefined {
  return cachedCatalog;
}

export function getSkillsCatalogStoreVersion(): number {
  return storeVersion;
}

export function publishSkillsCatalog(catalog: SkillsCatalog): void {
  cachedCatalog = catalog;
  storeVersion += 1;
  notifyListeners();
}

export function subscribeToSkillsCatalog(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetSkillsCatalogStore(): void {
  userScope = null;
  clearCatalog();
}

function fetchSkillsCatalog(): Promise<SkillsCatalog> {
  const requestGeneration = storeGeneration;
  const requestPromise = Promise.all([
    listPersonalSkills(),
    listDirectorySkills(),
  ])
    .then(([personal, directory]) => {
      const catalog = { personal, directory };
      if (requestGeneration === storeGeneration) {
        publishSkillsCatalog(catalog);
        lastFetchedAt = Date.now();
      }
      return catalog;
    })
    .catch((error: unknown) => {
      if (cachedCatalog) {
        return cachedCatalog;
      }
      throw error;
    })
    .finally(() => {
      if (inFlightPromise === requestPromise) {
        inFlightPromise = null;
      }
    });

  inFlightPromise = requestPromise;
  return requestPromise;
}

/**
 * Return the cached catalog when present. The first load still fetches; later
 * callers must use `refreshSkillsCatalog` to pick up skills created elsewhere.
 */
export async function ensureSkillsCatalog(): Promise<SkillsCatalog> {
  if (cachedCatalog) return cachedCatalog;
  if (inFlightPromise) return inFlightPromise;
  return fetchSkillsCatalog();
}

export async function refreshSkillsCatalog(options?: {
  force?: boolean;
}): Promise<SkillsCatalog> {
  if (inFlightPromise) return inFlightPromise;
  if (
    !options?.force &&
    cachedCatalog &&
    lastFetchedAt > 0 &&
    Date.now() - lastFetchedAt < SKILLS_CATALOG_REFRESH_COOLDOWN_MS
  ) {
    return cachedCatalog;
  }
  return fetchSkillsCatalog();
}
